import { useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "../../api/client";
import { useAuthStore } from "../../store/authStore";
import Spinner from "../../components/Spinner";

type MonthlyFortune = {
  range: string;
  month_pillar: string;
  month_gan?: string;
  month_ji?: string;
  fortune: string;
};

type YearlyReportData = {
  year: number;
  cached: boolean;
  overall: string;
  love: string;
  career: string;
  wealth: string;
  health: string;
  monthly: MonthlyFortune[];
};

const CATEGORY_META = [
  { key: "love",    icon: "💑", label: "연애·결혼" },
  { key: "career",  icon: "💼", label: "직업·사업" },
  { key: "wealth",  icon: "💰", label: "재물·재테크" },
  { key: "health",  icon: "🌿", label: "건강" },
] as const;

const TODAY_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = [TODAY_YEAR - 1, TODAY_YEAR, TODAY_YEAR + 1, TODAY_YEAR + 2];

export default function YearlyReport() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();

  const [selectedYear, setSelectedYear] = useState(TODAY_YEAR);
  const [data, setData] = useState<YearlyReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isAuthenticated) {
    navigate("/login");
    return null;
  }

  async function handleGenerate() {
    setError(null);
    setData(null);
    setLoading(true);
    try {
      const res = await client.get(`/features/yearly-report/${selectedYear}`);
      setData(res.data);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? "신년운세 리포트를 불러올 수 없습니다.");
    } finally {
      setLoading(false);
    }
  }

  function formatRange(range: string) {
    const [start, end] = range.split("~");
    const fmt = (d: string) => d.slice(5).replace("-", "/");
    return `${fmt(start)} ~ ${fmt(end)}`;
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg)] p-4 sm:p-6">
      <div className="max-w-lg mx-auto">
        {/* 헤더 */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate("/dashboard")}
            className="text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] p-1"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-xl font-bold text-[var(--color-text-primary)]">📜 신년운세 리포트</h1>
            <p className="text-[var(--color-text-tertiary)] text-xs">연간 총평 + 분야별 + 월별 운세</p>
          </div>
        </div>

        {/* 연도 선택 */}
        {!data && !loading && (
          <div className="bg-[var(--color-surface)] rounded-2xl border border-[var(--color-border-default)] p-6 mb-4">
            <p className="text-sm text-[var(--color-text-tertiary)] mb-4 text-center">
              운세를 확인할 연도를 선택하세요
            </p>
            <div className="grid grid-cols-4 gap-2 mb-6">
              {YEAR_OPTIONS.map((y) => (
                <button
                  key={y}
                  onClick={() => setSelectedYear(y)}
                  className={`py-2 rounded-xl text-sm font-semibold border transition-all ${
                    selectedYear === y
                      ? "bg-[var(--color-accent)] text-[var(--color-primary)] border-[var(--color-accent)]"
                      : "bg-[var(--color-bg)] text-[var(--color-text-tertiary)] border-[var(--color-border-default)] hover:border-[var(--color-border-accent)]"
                  }`}
                >
                  {y}년
                </button>
              ))}
            </div>
            <button
              onClick={handleGenerate}
              className="w-full py-3 bg-[var(--color-accent)] text-[var(--color-primary)] rounded-xl font-semibold hover:opacity-90 transition-opacity"
            >
              {selectedYear}년 운세 리포트 보기
            </button>
          </div>
        )}

        {/* 로딩 */}
        {loading && (
          <div className="flex flex-col items-center py-20 gap-4">
            <Spinner size="lg" />
            <p className="text-sm text-[var(--color-text-tertiary)]">럭키가 {selectedYear}년 운명을 찾아오는 중이에요</p>
          </div>
        )}

        {/* 에러 */}
        {error && !loading && (
          <div className="text-center py-12">
            <p className="text-4xl mb-3">📜</p>
            <p className="text-[var(--color-text-tertiary)] text-sm mb-4">{error}</p>
            <button
              onClick={() => setError(null)}
              className="px-4 py-2 bg-[var(--color-accent)] text-[var(--color-primary)] rounded-xl text-sm"
            >
              다시 시도
            </button>
          </div>
        )}

        {/* 결과 */}
        {data && !loading && (
          <div className="space-y-4">
            {/* 연도 헤더 + 다른 연도 버튼 */}
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-[var(--color-text-primary)]">{data.year}년 운세 리포트</h2>
              <button
                onClick={() => { setData(null); setError(null); }}
                className="text-xs text-[var(--color-accent)] underline"
              >
                다른 연도 선택
              </button>
            </div>
            {data.cached && (
              <p className="text-[10px] text-[var(--color-text-disabled)] -mt-2">저장된 리포트를 불러왔습니다.</p>
            )}

            {/* 연간 총평 */}
            <div className="bg-[var(--color-surface)] rounded-2xl border border-[var(--color-border-default)] p-5">
              <h3 className="text-sm font-bold text-[var(--color-accent)] mb-2">📋 연간 총평</h3>
              <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed whitespace-pre-wrap">{data.overall}</p>
            </div>

            {/* 분야별 운세 */}
            <div className="bg-[var(--color-surface)] rounded-2xl border border-[var(--color-border-default)] p-5">
              <h3 className="text-sm font-bold text-[var(--color-accent)] mb-3">🔮 분야별 운세</h3>
              <div className="space-y-3">
                {CATEGORY_META.map(({ key, icon, label }) => (
                  <div key={key} className="flex gap-3">
                    <span className="text-lg shrink-0">{icon}</span>
                    <div>
                      <p className="text-xs font-semibold text-[var(--color-text-tertiary)] mb-0.5">{label}</p>
                      <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">
                        {data[key as keyof Pick<YearlyReportData, "love" | "career" | "wealth" | "health">]}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 월별 운세 */}
            <div className="bg-[var(--color-surface)] rounded-2xl border border-[var(--color-border-default)] p-5">
              <h3 className="text-sm font-bold text-[var(--color-accent)] mb-3">📅 절기별 월운</h3>
              <div className="space-y-2">
                {data.monthly.map((m, i) => (
                  <div key={i} className="flex gap-3 py-2 border-b border-[var(--color-border-default)] last:border-0">
                    <div className="shrink-0 w-14 text-center">
                      <span className="text-xs font-bold text-[var(--color-primary)] bg-[var(--color-accent)] rounded-lg px-2 py-0.5">
                        {m.month_pillar.replace(/월$/, '')}월
                      </span>
                    </div>
                    <div className="flex-1">
                      <p className="text-[10px] text-[var(--color-text-disabled)] mb-0.5">{formatRange(m.range)}</p>
                      <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">{m.fortune}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
